import { useEffect, useState } from "react";
import { CustomIcon } from "../../UI";
import "./ToggleInfo.scss";
import { NavLink } from "react-bootstrap";
const ToggleInfo = ({ item, itemType, toggleData, expanded }) => {
  const [activeType, setActiveType] = useState('vehicle'); // 'vehicle' or 'freight'
  const [activeShipmentIndex, setActiveShipmentIndex] = useState(0);
  
  useEffect(()=>{
    if (itemType === 'loads' && item) {
      const load = item.load || item;
      const hasVehicle = load.vehicle?.shipment?.length > 0;
      const hasFreight = load.freight?.shipment?.length > 0;
      if (hasVehicle) {
        setActiveType('vehicle');
        setActiveShipmentIndex(0);
      } else if (hasFreight) {
        setActiveType('freight');
        setActiveShipmentIndex(0);
      }
    }
  }, [item, itemType])



  if (!expanded || !item) {
    return null;
  }

  const formatDate = (date) => {
    if (!date) return '--';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatAddress = (address) => {
    if (!address) return '--';
    const parts = [];
    if (address.name) parts.push(address.name);
    if (address.address) parts.push(address.address);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode || address.zip) parts.push(address.zipCode || address.zip);
    return parts.length > 0 ? parts.join(', ') : '--';
  };

  const formatEquipmentSummary = (equipment = []) => {
    if (!Array.isArray(equipment) || equipment.length === 0) return '';
    return equipment
      .map(item => {
        const count = Array.isArray(item.sizes) ? item.sizes.length : 0;
        const label = count === 1 ? 'unit' : 'units';
        return `${item.type} ${count} ${label}`;
      })
      .join(', ');
  };

  // Parse currency string (e.g., "$1,000.00") or number to float
  const parseCurrency = (value) => {
    if (typeof value === 'number') return value;
    if (!value || value === 'N/A' || value === '--') return 0;
    // Remove $, commas, and whitespace, then parse
    const cleaned = String(value).replace(/[$,\s]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const renderLoadsToggleInfo = () => {

    const load = item.load || item;
    const carrier = load.carrier || {};
    
    const vehicleShipments = load.vehicle?.shipment || [];
    const freightShipments = load.freight?.shipment || [];
    
    const hasVehicle = vehicleShipments.length > 0;
    const hasFreight = freightShipments.length > 0;
    const hasBothTypes = hasVehicle && hasFreight;
    
    // Get current active shipment
    const currentShipments = activeType === 'vehicle' ? vehicleShipments : freightShipments;
    const currentShipment = currentShipments[activeShipmentIndex] || {};
    const totalShipments = currentShipments.length;

    // Navigation functions
    const goToPreviousShipment = () => {
      if (activeShipmentIndex > 0) {
        setActiveShipmentIndex(activeShipmentIndex - 1);
      }
    };

    const goToNextShipment = () => {
      if (activeShipmentIndex < totalShipments - 1) {
        setActiveShipmentIndex(activeShipmentIndex + 1);
      }
    };

    const switchType = (type) => {
      setActiveType(type);
      setActiveShipmentIndex(0);
    };

    return (
      <div className="toggle-info-content">
        {/* Type Selector - Show only if both types exist */}
        {hasBothTypes && (
          <div className="type-selector">
            <button
              type="button"
              className={`type-btn ${activeType === 'vehicle' ? 'active' : ''}`}
              onClick={() => switchType('vehicle')}
            >
              Vehicle {vehicleShipments.length > 1 && `(${vehicleShipments.length})`}
            </button>
            <button
              type="button"
              className={`type-btn ${activeType === 'freight' ? 'active' : ''}`}
              onClick={() => switchType('freight')}
            >
              Freight {freightShipments.length > 1 && `(${freightShipments.length})`}
            </button>
          </div>
        )}

        <div className="details-grid">
          {/* Shipments Details */}
          <div className="detail-section">
            <div className="detail-section-header">
              <h4 className="detail-title">
                {activeType === 'vehicle' ? 'Vehicle Details' : 'Freight Details'}
              </h4>
              {/* Slider Navigation - Show only if multiple shipments */}
              {totalShipments > 1 && (
                <div className="shipment-navigation">
                  <button
                    type="button"
                    className="nav-btn"
                    onClick={goToPreviousShipment}
                    disabled={activeShipmentIndex === 0}
                    title="Previous"
                  >
                    <CustomIcon name="left" size={16} />
                  </button>
                  <span className="shipment-counter">
                    {activeShipmentIndex + 1} / {totalShipments}
                  </span>
                  <button
                    type="button"
                    className="nav-btn"
                    onClick={goToNextShipment}
                    disabled={activeShipmentIndex === totalShipments - 1}
                    title="Next"
                  >
                    <CustomIcon name="right" size={16} />
                  </button>
                </div>
              )}
            </div>
            <div className="detail-content">
              {activeType === 'vehicle' ? (
                <>
                  <div className="detail-row">
                    <span className="detail-label">VIN:</span>
                    <span className="detail-value">{currentShipment.vin || '--'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Make:</span>
                    <span className="detail-value">{currentShipment.make || '--'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Model:</span>
                    <span className="detail-value">{currentShipment.model || '--'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Year:</span>
                    <span className="detail-value">{currentShipment.year || '--'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Value:</span>
                    <span className="detail-value">
                      {currentShipment.value ? `$${currentShipment.value}` : '--'}
                    </span>
                  </div>
                  {load.vehicle?.specialRequirements && (
                    <div className="detail-row full-width">
                      <span className="detail-label">Special Requirements:</span>
                      <span className="detail-value">{load.vehicle.specialRequirements}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Commodity:</span>
                    <span className="detail-value">{currentShipment.commodity || '--'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Dimensions:</span>
                    <span className="detail-value">
                      {currentShipment.dimensionsLength && currentShipment.dimensionsWidth && currentShipment.dimensionsHeight
                        ? `${currentShipment.dimensionsLength}" × ${currentShipment.dimensionsWidth}" × ${currentShipment.dimensionsHeight}"`
                        : '--'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Weight:</span>
                    <span className="detail-value">
                      {currentShipment.weight ? `${currentShipment.weight} lbs` : '--'}
                    </span>
                  </div>
                  {currentShipment.poNumber && (
                    <div className="detail-row">
                      <span className="detail-label">PO#:</span>
                      <span className="detail-value">{currentShipment.poNumber}</span>
                    </div>
                  )}
                  {currentShipment.pickupNumber && (
                    <div className="detail-row">
                      <span className="detail-label">Pickup #:</span>
                      <span className="detail-value">{currentShipment.pickupNumber}</span>
                    </div>
                  )}
                </>
              )}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div className="detail-row" style={{ flex: '1', minWidth: '200px' }}>
                  <span className="detail-label">Carrier:</span>
                  <span className="detail-value">{carrier.name || '--'}</span>
                </div>
                <div className="detail-row" style={{ flex: '1', minWidth: '200px' }}>
                  <span className="detail-label">Carrier contact:</span>
                  <span className="detail-value">{carrier.phoneNumber || '--'}</span>
                </div>
                <div className="detail-row" style={{ flex: '1', minWidth: '200px' }}>
                  <span className="detail-label">Carrier email:</span>
                  <span className="detail-value">{carrier.email || '--'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* LOG - Timeline based on pickup and delivery */}
          <div className="detail-section">
            <h4 className="detail-title">Route</h4>
            <div className={`log-timeline ${load.status === 'Cancelled' ? 'cancelled' : 'pending'}`}>
              {load.pickup && (
                <div className="log-item">
                  <div className={`log-dot ${
                    load.status === 'Cancelled' 
                      ? '' 
                      : (load.status === 'Delivered' || load.status === 'Picked Up' || load.status === 'Picked up')
                        ? 'completed'
                        : 'pending'
                  }`} />
                  <div className="log-content">
                    <div className="log-location">
                      {load.pickup.locationName || formatAddress(load.pickup.address)}
                    </div>
                    <div className="log-date">{formatDate(load.pickup.date || load.dates?.pickupDate)}</div>
                  </div>
                </div>
              )}
              {load.delivery && (
                <div className="log-item">
                  <div className={`log-dot ${
                    load.status === 'Cancelled'
                      ? ''
                      : load.status === 'Delivered'
                        ? 'completed'
                        : 'pending'
                  }`} />
                  <div className="log-content">
                    <div className="log-location">
                      {load.delivery.locationName || formatAddress(load.delivery.address)}
                    </div>
                    <div className="log-date">{formatDate(load.delivery.date || load.dates?.deliveryDate)}</div>
                  </div>
                </div>
              )}
              {!load.pickup && !load.delivery && (
                <div className="log-item">
                  <div className="log-content">
                    <div className="log-location">No location data available</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer Name */}
          <div className="detail-section">
            <h4 className="detail-title">Customer Name</h4>
            <div className="detail-content">
              <div className="customer-name">{load.customer?.companyName || load.customerCompanyName || '--'}</div>
              
              {/* Customer Email */}
              {load.customer?.email && (
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{load.customer.email}</span>
                </div>
              )}
              
              {/* Customer Emails (additional) */}
              {load.customerEmails && load.customerEmails.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Additional emails:</span>
                  <span className="detail-value">{load.customerEmails.join(', ')}</span>
                </div>
              )}
              
              {/* Customer Phone */}
              {load.customer?.phoneNumber && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{load.customer.phoneNumber}</span>
                </div>
              )}
              
              {/* Customer Address */}
              {load.customer?.customerAddress && (
                <div className="detail-row">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">{formatAddress(load.customer.customerAddress)}</span>
                </div>
              )}
              
              {/* Customer Rate */}
              {load.customerRate && parseCurrency(load.customerRate) > 0 && (
                <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                  <span className="detail-label">Customer Rate:</span>
                  <span className="detail-value">
                    ${parseCurrency(load.customerRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {/* Carrier Rate */}
              {load.carrierRate && parseCurrency(load.carrierRate) > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Carrier Rate:</span>
                  <span className="detail-value">
                    ${parseCurrency(load.carrierRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {/* Fees */}
              {load.fees && Array.isArray(load.fees) && load.fees.length > 0 && load.fees.some(fee => {
                const feeAmount = parseCurrency(fee.customerRate || fee.total || 0);
                return feeAmount > 0;
              }) && (
                <>
                  <div className="detail-row full-width" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    <span className="detail-label"><strong>Fees:</strong></span>
                  </div>
                  {load.fees.map((fee, index) => {
                    const feeAmount = parseCurrency(fee.customerRate || fee.total || 0);
                    if (feeAmount <= 0) return null;
                    return (
                      <div key={index} className="detail-row" style={{ marginLeft: '12px' }}>
                        <span className="detail-label">{fee.type || 'Fee'}:</span>
                        <span className="detail-value">
                          ${feeAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUsersToggleInfo = () => {
    const user = item;

    return (
      <div className="toggle-info-content">
        <div className="details-grid">
          <div className="detail-section">
            <h4 className="detail-title">User Details</h4>
            <div className="detail-content">
              {user.role && (
                <div className="detail-row">
                  <span className="detail-label">Role:</span>
                  <span className="detail-value">
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </span>
                </div>
              )}
              {user.companyName && (
                <div className="detail-row">
                  <span className="detail-label">Company:</span>
                  <span className="detail-value">{user.companyName}</span>
                </div>
              )}
              {user.createdAt && (
                <div className="detail-row">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">{formatDate(user.createdAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomersToggleInfo = () => {
    const customer = item;
    const address = customer.customerAddress || {};
    const fullAddress = formatAddress({
      address: address.address,
      city: address.city || customer.city,
      state: address.state || customer.state,
      zipCode: address.zipCode || customer.zipCode
    });

    return (
      <div className="toggle-info-content">
        <div className="details-grid">
          {/* Company Information */}
          <div className="detail-section">
            <h4 className="detail-title">Company Information</h4>
            <div className="detail-content">
              {customer.companyName && (
                <div className="detail-row">
                  <span className="detail-label">Company Name:</span>
                  <span className="detail-value">{customer.companyName}</span>
                </div>
              )}
              {customer.createdAt && (
                <div className="detail-row">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">{formatDate(customer.createdAt)}</span>
                </div>
              )}
              {customer.updatedAt && (
                <div className="detail-row">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">{formatDate(customer.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="detail-section">
            <h4 className="detail-title">Contact Information</h4>
            <div className="detail-content">
              {fullAddress && fullAddress !== '--' && (
                <div className="detail-row full-width">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">{fullAddress}</span>
                </div>
              )}
              {address.city && (
                <div className="detail-row">
                  <span className="detail-label">City:</span>
                  <span className="detail-value">{address.city}</span>
                </div>
              )}
              {address.state && (
                <div className="detail-row">
                  <span className="detail-label">State:</span>
                  <span className="detail-value">{address.state}</span>
                </div>
              )}
              {address.zipCode && (
                <div className="detail-row">
                  <span className="detail-label">Zip Code:</span>
                  <span className="detail-value">{address.zipCode}</span>
                </div>
              )}
              {customer.phoneNumber && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{customer.phoneNumber}</span>
                </div>
              )}
              {customer.email && (
                <div className="detail-row full-width">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">
                    {customer.email}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Information */}
          {(customer.paymentMethod || customer.paymentTerms || (customer.creditLimit !== undefined && customer.creditLimit !== null)) && (
            <div className="detail-section">
              <h4 className="detail-title">Payment Information</h4>
              <div className="detail-content">
                {customer.paymentMethod && (
                  <div className="detail-row">
                    <span className="detail-label">Payment Method:</span>
                    <span className="detail-value">{customer.paymentMethod}</span>
                  </div>
                )}
                {customer.paymentTerms && (
                  <div className="detail-row">
                    <span className="detail-label">DTP:</span>
                    <span className="detail-value">{customer.paymentTerms}</span>
                  </div>
                )}
                {customer.creditLimit !== undefined && customer.creditLimit !== null && (
                  <div className="detail-row">
                    <span className="detail-label">Credit Limit:</span>
                    <span className="detail-value">
                      ${typeof customer.creditLimit === 'number' 
                        ? customer.creditLimit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : parseFloat(customer.creditLimit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCarriersToggleInfo = () => {
    const carrier = item;
    const address = carrier.address || {};
    const fullAddress = formatAddress({
      address: address.address || carrier.address,
      city: address.city || carrier.city,
      state: address.state || carrier.state,
      zipCode: address.zipCode || carrier.zipCode
    });

    return (
      <div className="toggle-info-content">
        <div className="details-grid">
          {/* Company Information */}
          <div className="detail-section">
            <h4 className="detail-title">Company Information</h4>
            <div className="detail-content">
              {carrier.companyName && (
                <div className="detail-row">
                  <span className="detail-label">Company Name:</span>
                  <span className="detail-value">{carrier.companyName}</span>
                </div>
              )}
              {carrier.mcNumber && (
                <div className="detail-row">
                  <span className="detail-label">MC#:</span>
                  <span className="detail-value">{carrier.mcNumber}</span>
                </div>
              )}
              {carrier.dotNumber && (
                <div className="detail-row">
                  <span className="detail-label">DOT#:</span>
                  <span className="detail-value">{carrier.dotNumber}</span>
                </div>
              )}
              {carrier.createdAt && (
                <div className="detail-row">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">{formatDate(carrier.createdAt)}</span>
                </div>
              )}
              {carrier.updatedAt && (
                <div className="detail-row">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">{formatDate(carrier.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="detail-section">
            <h4 className="detail-title">Contact Information</h4>
            <div className="detail-content">
              {fullAddress && fullAddress !== '--' && (
                <div className="detail-row full-width">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">{fullAddress}</span>
                </div>
              )}
              {carrier.phoneNumber && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{carrier.phoneNumber}</span>
                </div>
              )}
              {carrier.email && (
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{carrier.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Equipment & Specifications */}
          <div className="detail-section">
            <h4 className="detail-title">Equipment & Specifications</h4>
            <div className="detail-content">
              {carrier.equipment && Array.isArray(carrier.equipment) && carrier.equipment.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Equipment:</span>
                  <span className="detail-value">{formatEquipmentSummary(carrier.equipment)}</span>
                </div>
              )}
              {carrier.capabilities && Array.isArray(carrier.capabilities) && carrier.capabilities.length > 0 && (
                <div className="detail-row full-width">
                  <span className="detail-label">Capabilities:</span>
                  <span className="detail-value">
                    {carrier.capabilities.join(', ')}
                  </span>
                </div>
              )}
              {carrier.certifications && Array.isArray(carrier.certifications) && carrier.certifications.length > 0 && (
                <div className="detail-row full-width">
                  <span className="detail-label">Certifications:</span>
                  <span className="detail-value">
                    {carrier.certifications.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentsToggleInfo = () => {
    if (!toggleData) return null;

    return (
      <div className="toggle-info-content">
        <div className="details-grid">
          <div className="detail-section">
            <h4 className="detail-title">Payment Details</h4>
            <div className="detail-content">
              {toggleData.transactionId && (
                <div className="detail-row">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">{toggleData.transactionId}</span>
                </div>
              )}
              {toggleData.method && (
                <div className="detail-row">
                  <span className="detail-label">Method:</span>
                  <span className="detail-value">{toggleData.method}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentsReceivableToggleInfo = () => {
    const payment = item;
    const customer = payment.customer || {};
    const load = payment.load || {};

    return (
      <div className="toggle-info-content">
        <div className="details-grid">
          {/* Customer Information */}
          <div className="detail-section">
            <h4 className="detail-title">Customer Information</h4>
            <div className="detail-content">
              <div className="detail-row">
                <span className="detail-label">Company Name:</span>
                <span className="detail-value">{customer.companyName || '--'}</span>
              </div>
              {customer.email && (
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{customer.email}</span>
                </div>
              )}
              {customer.phoneNumber && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{customer.phoneNumber}</span>
                </div>
              )}
              {customer.customerAddress && (
                <div className="detail-row full-width">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">
                    {[
                      customer.customerAddress.address,
                      customer.customerAddress.city,
                      customer.customerAddress.state,
                      customer.customerAddress.zipCode
                    ].filter(Boolean).join(', ') || '--'}
                  </span>
                </div>
              )}
              {/* Payment Information */}
              {(customer.paymentMethod || customer.paymentTerms || (customer.creditLimit !== undefined && customer.creditLimit !== null)) && (
                <>
                  <div className="detail-row full-width" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                    <span className="detail-label" style={{ fontWeight: 'bold', color: '#666' }}>Payment Information:</span>
                  </div>
                  {customer.paymentMethod && (
                    <div className="detail-row">
                      <span className="detail-label">Payment Method:</span>
                      <span className="detail-value">{customer.paymentMethod}</span>
                    </div>
                  )}
                  {customer.paymentTerms && (
                    <div className="detail-row">
                      <span className="detail-label">DTP:</span>
                      <span className="detail-value">{customer.paymentTerms}</span>
                    </div>
                  )}
                  {customer.creditLimit !== undefined && customer.creditLimit !== null && (
                    <div className="detail-row">
                      <span className="detail-label">Credit Limit:</span>
                      <span className="detail-value">
                        ${typeof customer.creditLimit === 'number' 
                          ? customer.creditLimit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : parseFloat(customer.creditLimit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        }
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Payment Amount Details */}
          <div className="detail-section">
            <h4 className="detail-title">Payment Amount Details</h4>
            <div className="detail-content">
              {/* Customer Rate */}
              {(load.customerRate !== undefined && load.customerRate !== null && parseCurrency(load.customerRate) > 0) ||
              (payment.customerRate !== undefined && payment.customerRate !== null && parseCurrency(payment.customerRate) > 0) ? (
                <div className="detail-row">
                  <span className="detail-label">Original Customer Rate:</span>
                  <span className="detail-value">
                    ${parseCurrency(load.customerRate ?? payment.customerRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ) : null}
              
              {/* Additional Fees */}
              {payment.fees && Array.isArray(payment.fees) && payment.fees.length > 0 && payment.fees.some(fee => {
                const feeAmount = parseCurrency(fee.customerRate || fee.total || 0);
                return feeAmount > 0;
              }) && (
                <>
                  <div className="detail-row full-width" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    <span className="detail-label">Additional Fees:</span>
                  </div>
                  {payment.fees.map((fee, index) => {
                    const feeAmount = parseCurrency(fee.customerRate || fee.total || 0);
                    if (feeAmount <= 0) return null;
                    return (
                      <div key={index} className="detail-row" style={{ marginLeft: '12px' }}>
                        <span className="detail-label">{fee.type || 'Fee'}:</span>
                        <span className="detail-value">
                          ${feeAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
              
              {/* TONU */}
              {payment.tonu && payment.tonu.enabled && payment.tonu.customerRate && parseCurrency(payment.tonu.customerRate) > 0 && (
                <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                  <span className="detail-label">TONU:</span>
                  <span className="detail-value">
                    ${parseCurrency(payment.tonu.customerRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {/* Total Amount - Calculate if not provided */}
              {(() => {
                const baseRate = parseCurrency(load.customerRate ?? payment.customerRate ?? 0);
                const feesTotal = Array.isArray(payment.fees) && payment.fees.length > 0
                  ? payment.fees.reduce((sum, fee) => {
                      const feeAmount = parseCurrency(fee.customerRate || fee.total || 0);
                      return sum + feeAmount;
                    }, 0)
                  : 0;
                const tonuAmount = (payment.tonu?.enabled && payment.tonu?.customerRate) 
                  ? parseCurrency(payment.tonu.customerRate) 
                  : 0;
                const calculatedTotal = baseRate + feesTotal + tonuAmount;
                const displayTotal = payment.totalAmount 
                  ? parseCurrency(payment.totalAmount) 
                  : calculatedTotal;
                
                if (displayTotal <= 0) return null;
                
                return (
                  <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    <span className="detail-label"><strong>Total Amount:</strong></span>
                    <span className="detail-value">
                      <strong>${displayTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="detail-section">
            <h4 className="detail-title">Invoice Details</h4>
            <div className="detail-content">
              {payment.orderId && (
                <div className="detail-row">
                  <span className="detail-label">Order ID:</span>
                  <span className="detail-value">{payment.orderId}</span>
                </div>
              )}
              {payment.loadId && (
                <div className="detail-row">
                  <span className="detail-label">Load ID:</span>
                  <span className="detail-value">{payment.loadId}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Invoice Number:</span>
                <span className="detail-value">{payment.invoiceNumber || 'Pending'}</span>
              </div>
              {payment.invoiceAt && (
                <div className="detail-row">
                  <span className="detail-label">Invoice Date:</span>
                  <span className="detail-value">{formatDate(payment.invoiceAt)}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Due Date:</span>
                <span className="detail-value" style={{ color: payment.isOverdue ? '#ef4444' : 'inherit' }}>
                  {formatDate(payment.dueAt)}
                  {payment.isOverdue && ' (Overdue)'}
                </span>
              </div>
              {payment.deadlineDays && (
                <div className="detail-row">
                  <span className="detail-label">Deadline Days:</span>
                  <span className="detail-value">{payment.deadlineDays} days</span>
                </div>
              )}
              {payment.paymentMethod && (
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{payment.paymentMethod}</span>
                </div>
              )}
              {payment.paymentLink && (
                <div className="detail-row">
                  <span className="detail-label">Payment Link:</span>
                  <span className="detail-value">
                    <a href={payment.paymentLink} target="_blank" rel="noopener noreferrer">
                      {payment.paymentLink}
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Load Information */}
          {(load && (load.orderId || load._id)) || payment.orderId || payment.loadId ? (
            <div className="detail-section full-width">
              <h4 className="detail-title">Load Information</h4>
              <div className="detail-content">
                {(payment.orderId || (load && load.orderId)) && (
                  <div className="detail-row">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">{payment.orderId || load?.orderId || '--'}</span>
                  </div>
                )}
                {payment.loadId && (
                  <div className="detail-row">
                    <span className="detail-label">Load ID:</span>
                    <span className="detail-value">{payment.loadId}</span>
                  </div>
                )}
                {load?.status && (
                  <div className="detail-row">
                    <span className="detail-label">Load Status:</span>
                    <span className="detail-value">{load.status}</span>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Notes */}
          {payment.notes && (
            <div className="detail-section full-width">
              <h4 className="detail-title">Notes</h4>
              <div className="detail-content">
                <div className="detail-row full-width">
                  <span className="detail-value">{payment.notes}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPaymentsPayableToggleInfo = () => {
    const payment = item;
    const carrier = payment.carrier || {};
    const load = payment.load || {};
    const deductions = payment.deductions || {};

    return (
      <div className="toggle-info-content">
        <div className="details-grid">
          {/* Carrier Information */}
          <div className="detail-section">
            <h4 className="detail-title">Carrier Information</h4>
            <div className="detail-content">
              <div className="detail-row">
                <span className="detail-label">Company Name:</span>
                <span className="detail-value">{carrier.companyName || carrier.name || '--'}</span>
              </div>
              {carrier.mcNumber && (
                <div className="detail-row">
                  <span className="detail-label">MC#:</span>
                  <span className="detail-value">{carrier.mcNumber}</span>
                </div>
              )}
              {carrier.dotNumber && (
                <div className="detail-row">
                  <span className="detail-label">DOT#:</span>
                  <span className="detail-value">{carrier.dotNumber}</span>
                </div>
              )}
              {carrier.email && (
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{carrier.email}</span>
                </div>
              )}
              {carrier.phoneNumber && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{carrier.phoneNumber}</span>
                </div>
              )}
              {carrier.address && (
                <div className="detail-row full-width">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">
                    {[
                      carrier.address.address,
                      carrier.address.city,
                      carrier.address.state,
                      carrier.address.zipCode
                    ].filter(Boolean).join(', ') || '--'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Amount Details */}
          <div className="detail-section">
            <h4 className="detail-title">Payment Amount Details</h4>
            <div className="detail-content">
              {/* Carrier Rate */}
              {(load.carrierRate !== undefined && load.carrierRate !== null && parseCurrency(load.carrierRate) > 0) ||
              (payment.carrierRate !== undefined && payment.carrierRate !== null && parseCurrency(payment.carrierRate) > 0) ? (
                <div className="detail-row">
                  <span className="detail-label">Original Carrier Rate:</span>
                  <span className="detail-value">
                    ${parseCurrency(load.carrierRate ?? payment.carrierRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ) : null}
              
              {/* Additional Fees */}
              {payment.fees && Array.isArray(payment.fees) && payment.fees.length > 0 && payment.fees.some(fee => {
                const feeAmount = parseCurrency(fee.carrierRate || fee.total || 0);
                return feeAmount > 0;
              }) && (
                <>
                  <div className="detail-row full-width" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    <span className="detail-label">Additional Fees:</span>
                  </div>
                  {payment.fees.map((fee, index) => {
                    const feeAmount = parseCurrency(fee.carrierRate || fee.total || 0);
                    if (feeAmount <= 0) return null;
                    return (
                      <div key={index} className="detail-row" style={{ marginLeft: '12px' }}>
                        <span className="detail-label">{fee.type || 'Fee'}:</span>
                        <span className="detail-value">
                          ${feeAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
              
              {/* TONU */}
              {payment.tonu && payment.tonu.enabled && payment.tonu.carrierRate && parseCurrency(payment.tonu.carrierRate) > 0 && (
                <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                  <span className="detail-label">TONU:</span>
                  <span className="detail-value">
                    ${parseCurrency(payment.tonu.carrierRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {/* Total Amount - Calculate if not provided */}
              {(() => {
                const baseRate = parseCurrency(load.carrierRate ?? payment.carrierRate ?? 0);
                const feesTotal = Array.isArray(payment.fees) && payment.fees.length > 0
                  ? payment.fees.reduce((sum, fee) => {
                      const feeAmount = parseCurrency(fee.carrierRate || fee.total || 0);
                      return sum + feeAmount;
                    }, 0)
                  : 0;
                const tonuAmount = (payment.tonu?.enabled && payment.tonu?.carrierRate) 
                  ? parseCurrency(payment.tonu.carrierRate) 
                  : 0;
                const calculatedTotal = baseRate + feesTotal + tonuAmount;
                const displayTotal = payment.totalAmount 
                  ? parseCurrency(payment.totalAmount) 
                  : calculatedTotal;
                
                if (displayTotal <= 0) return null;
                
                return (
                  <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    <span className="detail-label"><strong>Total Amount:</strong></span>
                    <span className="detail-value">
                      <strong>${displayTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Bank Information */}
          {(payment.bank || payment.routing || payment.accountNumber) && (
            <div className="detail-section">
              <h4 className="detail-title">Bank Information</h4>
              <div className="detail-content">
                {payment.bank && (
                  <div className="detail-row">
                    <span className="detail-label">Bank:</span>
                    <span className="detail-value">{payment.bank}</span>
                  </div>
                )}
                {payment.routing && (
                  <div className="detail-row">
                    <span className="detail-label">Routing Number:</span>
                    <span className="detail-value">{payment.routing}</span>
                  </div>
                )}
                {payment.accountNumber && (
                  <div className="detail-row">
                    <span className="detail-label">Account Number:</span>
                    <span className="detail-value">{payment.accountNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invoice Details */}
          <div className="detail-section">
            <h4 className="detail-title">Invoice Details</h4>
            <div className="detail-content">
              {payment.orderId && (
                <div className="detail-row">
                  <span className="detail-label">Order ID:</span>
                  <span className="detail-value">{payment.orderId}</span>
                </div>
              )}
              {payment.loadId && (
                <div className="detail-row">
                  <span className="detail-label">Load ID:</span>
                  <span className="detail-value">{payment.loadId}</span>
                </div>
              )}
              {payment.invoiceAt && (
                <div className="detail-row">
                  <span className="detail-label">Invoice Date:</span>
                  <span className="detail-value">{formatDate(payment.invoiceAt)}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Due Date:</span>
                <span className="detail-value" style={{ color: payment.isOverdue ? '#ef4444' : 'inherit' }}>
                  {formatDate(payment.dueAt)}
                  {payment.isOverdue && ' (Overdue)'}
                </span>
              </div>
              {payment.deadlineDays && (
                <div className="detail-row">
                  <span className="detail-label">Deadline Days:</span>
                  <span className="detail-value">{payment.deadlineDays} days</span>
                </div>
              )}
              {payment.paymentMethod && (
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{payment.paymentMethod}</span>
                </div>
              )}
            </div>
          </div>
          {/* Deductions */}
          {(deductions.quickPayFee || deductions.factoringFee || deductions.otherDeductions || deductions.totalDeductions) && (
            <div className="detail-section">
              <h4 className="detail-title">Deductions</h4>
              <div className="detail-content">
                {deductions.quickPayFee > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Quick Pay Fee:</span>
                    <span className="detail-value">-${deductions.quickPayFee.toLocaleString()}</span>
                  </div>
                )}
                {deductions.factoringFee > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Factoring Fee:</span>
                    <span className="detail-value">-${deductions.factoringFee.toLocaleString()}</span>
                  </div>
                )}
                {deductions.otherDeductions > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Other Deductions:</span>
                    <span className="detail-value">-${deductions.otherDeductions.toLocaleString()}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label"><strong>Total Deductions:</strong></span>
                  <span className="detail-value"><strong>-${(deductions.totalDeductions || 0).toLocaleString()}</strong></span>
                </div>
                {deductions.deductionNotes && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Notes:</span>
                    <span className="detail-value">{deductions.deductionNotes}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Load Information */}
          {(load && (load.orderId || load._id)) || payment.orderId || payment.loadId ? (
            <div className="detail-section full-width">
              <h4 className="detail-title">Load Information</h4>
              <div className="detail-content">
                {(payment.orderId || (load && load.orderId)) && (
                  <div className="detail-row">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">{payment.orderId || load?.orderId || '--'}</span>
                  </div>
                )}
                {payment.loadId && (
                  <div className="detail-row">
                    <span className="detail-label">Load ID:</span>
                    <span className="detail-value">{payment.loadId}</span>
                  </div>
                )}
                {load?.status && (
                  <div className="detail-row">
                    <span className="detail-label">Load Status:</span>
                    <span className="detail-value">{load.status}</span>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Notes */}
          {payment.notes && (
            <div className="detail-section full-width">
              <h4 className="detail-title">Notes</h4>
              <div className="detail-content">
                <div className="detail-row full-width">
                  <span className="detail-value">{payment.notes}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (itemType) {
      case 'loads':
        return renderLoadsToggleInfo();
      case 'users':
        return renderUsersToggleInfo();
      case 'customers':
        return renderCustomersToggleInfo();
      case 'carriers':
        return renderCarriersToggleInfo();
      case 'payments':
        return renderPaymentsToggleInfo();
      case 'paymentsReceivable':
        return renderPaymentsReceivableToggleInfo();
      case 'paymentsPayable':
        return renderPaymentsPayableToggleInfo();
      default:
        return null;
    }
  };

  return (
    <div className="toggle-info-expanded">
      {renderContent()}
    </div>
  );
};

export default ToggleInfo;
